import { useState, useEffect } from 'react';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  FilePlus,
  PencilSimple,
  Trash,
  CheckCircle,
  XCircle,
  UploadSimple,
  Gear,
  Link,
  ArrowsDownUp,
  Coin,
  FileArrowDown,
  FileArrowUp,
  ArrowsClockwise,
  Warning,
  Eye,
  CloudArrowDown,
  Receipt,
  CurrencyCircleDollar,
} from '@phosphor-icons/react';

interface EventLog {
  id: string;
  organization_id: string;
  object_type: string;
  object_id: string;
  event_type: string;
  description: string | null;
  event_metadata: Record<string, any> | null;
  user_id: string | null;
  created_at: string;
  user: {
    id: string;
    email: string;
    full_name: string | null;
  } | null;
}

interface EventHistoryProps {
  objectType: string;
  objectId: string;
  autoLoad?: boolean;
}

export default function EventHistory({ objectType, objectId, autoLoad = false }: EventHistoryProps) {
  const { t } = useI18n();
  const [events, setEvents] = useState<EventLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadEvents = async () => {
    if (hasLoaded) return; // Don't reload if already loaded

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/${objectType}/${objectId}/events`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
        setHasLoaded(true);
      } else {
        const errorData = await response.json().catch(() => ({ detail: t('events.failedToLoad', 'common') }));
        setError(errorData.detail || t('events.failedToLoad', 'common'));
      }
    } catch (err) {
      console.error('Error loading events:', err);
      setError(t('events.failedToLoad', 'common'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad) {
      loadEvents();
    }
  }, [autoLoad, objectId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getEventIcon = (eventType: string, metadata: Record<string, any> | null) => {
    const type = eventType.toLowerCase();

    // Payment-related events
    if (metadata?.payment_id || metadata?.payment_amount || type.includes('payment')) {
      return <Coin weight="duotone" className="w-6 h-6 text-green-600" />;
    }

    // Invoice-specific events
    if (type === 'uploaded') return <UploadSimple weight="duotone" className="w-6 h-6 text-blue-600" />;
    if (type === 'created') return <FilePlus weight="duotone" className="w-6 h-6 text-blue-600" />;
    if (type === 'updated' || type === 'modified') return <PencilSimple weight="duotone" className="w-6 h-6 text-amber-600" />;
    if (type === 'deleted') return <Trash weight="duotone" className="w-6 h-6 text-red-600" />;
    if (type === 'verified' || type === 'approved') return <CheckCircle weight="duotone" className="w-6 h-6 text-green-600" />;
    if (type === 'rejected') return <XCircle weight="duotone" className="w-6 h-6 text-red-600" />;
    if (type.includes('processing')) return <Gear weight="duotone" className="w-6 h-6 text-purple-600" />;
    if (type === 'matched') return <Link weight="duotone" className="w-6 h-6 text-indigo-600" />;
    if (type === 'imported_from_nav' || type === 'imported') return <FileArrowDown weight="duotone" className="w-6 h-6 text-cyan-600" />;
    if (type === 'enhanced_from_nav' || type === 'enhanced') return <CloudArrowDown weight="duotone" className="w-6 h-6 text-cyan-600" />;
    if (type === 'exported') return <FileArrowUp weight="duotone" className="w-6 h-6 text-orange-600" />;
    if (type.includes('synced') || type.includes('sync')) return <ArrowsClockwise weight="duotone" className="w-6 h-6 text-teal-600" />;
    if (type === 'posted') return <CurrencyCircleDollar weight="duotone" className="w-6 h-6 text-green-600" />;
    if (type === 'voided' || type === 'reversed') return <Warning weight="duotone" className="w-6 h-6 text-orange-600" />;
    if (type === 'reconciled') return <Receipt weight="duotone" className="w-6 h-6 text-emerald-600" />;

    // Default
    return <Receipt weight="duotone" className="w-6 h-6 text-gray-600" />;
  };

  const formatEventType = (eventType: string, metadata: Record<string, any> | null) => {
    const type = eventType.toLowerCase();

    // Special handling for payment events
    if (metadata?.payment_id || metadata?.payment_amount) {
      if (type === 'created') return t('events.paymentCreated', 'common');
      if (type === 'posted') return t('events.paymentPosted', 'common');
      if (type === 'updated') return t('events.paymentUpdated', 'common');
      if (type === 'voided') return t('events.paymentVoided', 'common');
      if (type === 'reversed') return t('events.paymentReversed', 'common');
      if (type === 'deleted') return t('events.paymentDeleted', 'common');
    }

    // Special formatting for specific event types
    const specialFormats: Record<string, string> = {
      'imported_from_nav': t('events.importedFromNav', 'common'),
      'enhanced_from_nav': t('events.enhancedFromNav', 'common'),
      'processing_started': t('events.processingStarted', 'common'),
      'processing_completed': t('events.processingCompleted', 'common'),
      'processing_failed': t('events.processingFailed', 'common'),
      'reprocessing_started': 'Reprocessing Started',
      'reprocessing_completed': 'Reprocessing Completed',
      'delivery_note_processed': 'Delivery Note Processed',
      'delivery_note_processing_failed': 'Delivery Note Processing Failed',
    };

    if (specialFormats[type]) {
      return specialFormats[type];
    }

    // Convert snake_case or SCREAMING_SNAKE_CASE to Title Case
    return eventType
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Trigger load when component becomes visible (if not auto-loading)
  useEffect(() => {
    if (!autoLoad && !hasLoaded) {
      loadEvents();
    }
  }, []);

  if (loading) {
    return (
      <div className="space-y-4 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-4xl mb-2">📋</div>
        <p className="text-muted-foreground">{t('events.noEvents', 'common')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {events.map((event, index) => (
        <div
          key={event.id}
          className={`flex gap-4 p-4  hover:bg-muted/50 transition-colors ${
            index !== events.length - 1 ? 'border-b' : ''
          }`}
        >
          {/* Event Icon */}
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-muted/30 flex items-center justify-center">
              {getEventIcon(event.event_type, event.event_metadata)}
            </div>
          </div>

          {/* Event Details */}
          <div className="flex-1 min-w-0">
            {/* Event Type and Time */}
            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className="font-medium text-sm">
                {formatEventType(event.event_type, event.event_metadata)}
              </h4>
              <time className="text-xs text-muted-foreground whitespace-nowrap">
                {formatDate(event.created_at)}
              </time>
            </div>

            {/* Description */}
            {event.description && (
              <p className="text-sm text-muted-foreground mb-2">
                {event.description}
              </p>
            )}

            {/* User Info */}
            {event.user && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {event.user.full_name || event.user.email}
                </span>
              </div>
            )}

            {/* Metadata (if present and not empty) */}
            {event.event_metadata && Object.keys(event.event_metadata).length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  {t('events.viewDetails', 'common')}
                </summary>
                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                  {JSON.stringify(event.event_metadata, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
